import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FaGraduationCap, FaEye, FaBullseye, FaCalendarAlt, FaImages, FaBullhorn, FaEnvelope, FaArrowDown } from 'react-icons/fa';
import Testimonials from '../components/Testimonials';
import Counters from '../components/Counters';
import SocialShare from '../components/SocialShare';

const Home = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const quickLinks = [
    {
      title: 'Events',
      description: 'Stay updated with our latest events and activities',
      icon: FaCalendarAlt,
      path: '/events',
      color: 'blue'
    },
    {
      title: 'Gallery',
      description: 'Explore moments from our school life',
      icon: FaImages,
      path: '/gallery',
      color: 'green'
    },
    {
      title: 'Announcements',
      description: 'Latest news and important updates',
      icon: FaBullhorn,
      path: '/announcements',
      color: 'purple'
    },
    {
      title: 'Contact',
      description: 'Get in touch with us',
      icon: FaEnvelope,
      path: '/contact',
      color: 'red'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-600 hover:bg-blue-700 text-blue-600 hover:text-blue-700',
      green: 'bg-green-600 hover:bg-green-700 text-green-600 hover:text-green-700',
      purple: 'bg-purple-600 hover:bg-purple-700 text-purple-600 hover:text-purple-700',
      red: 'bg-red-600 hover:bg-red-700 text-red-600 hover:text-red-700'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Helmet>
        <title>Rasoha Academy | Home - Excellence in Education</title>
        <meta name="description" content="Welcome to Rasoha Academy Narok. Empowering students with quality education and fostering a culture of excellence, innovation, and community service." />
        <meta name="keywords" content="Rasoha Academy, Narok, School, Education, Kenya, Private School, Academic Excellence, Student Development" />
        <meta property="og:title" content="Rasoha Academy | Home - Excellence in Education" />
        <meta property="og:description" content="Welcome to Rasoha Academy Narok. Empowering students with quality education and fostering a culture of excellence." />
        <meta property="og:url" content="https://rasohaacademy.com" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="text-center" data-aos="fade-up">
            {/* School Logo/Icon */}
            <div className="mb-8" data-aos="zoom-in" data-aos-delay="200">
              <div className="w-24 h-24 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaGraduationCap className="w-12 h-12 text-white" />
              </div>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight" data-aos="fade-up" data-aos-delay="300">
              Welcome to
              <span className="block text-yellow-300">Rasoha School</span>
            </h1>

            {/* Subheading */}
            <p className="text-xl md:text-2xl lg:text-3xl mb-8 max-w-4xl mx-auto leading-relaxed opacity-90" data-aos="fade-up" data-aos-delay="400">
              Empowering students with quality education and fostering a culture of excellence, 
              innovation, and community service.
            </p>

            {/* CTA Button */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-aos="fade-up" data-aos-delay="500">
              <button
                onClick={() => scrollToSection('motto-section')}
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                Explore More
                <FaArrowDown className="inline ml-2 w-5 h-5" />
              </button>
              <Link
                to="/about"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                Learn About Us
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <FaArrowDown className="w-6 h-6 text-white opacity-70" />
        </div>
      </section>

      {/* Motto, Vision & Mission Section */}
      <section id="motto-section" className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              The foundation of our educational philosophy
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Our Motto */}
            <div 
              className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FaGraduationCap className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Our Motto
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  "Excellence in Education, Character in Action"
                </p>
              </div>
            </div>

            {/* Our Vision */}
            <div 
              className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900 dark:to-green-800 p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FaEye className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Our Vision
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  To be a leading institution that nurtures innovative thinkers and responsible global citizens.
                </p>
              </div>
            </div>

            {/* Our Mission */}
            <div 
              className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900 dark:to-purple-800 p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FaBullseye className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Our Mission
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  To provide comprehensive education that develops critical thinking, creativity, and strong character.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links / Highlights Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Quick Access
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Explore key areas of our school website
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickLinks.map((link, index) => (
              <div
                key={link.title}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 group"
                data-aos="zoom-in"
                data-aos-delay={index * 100}
              >
                <div className="p-6 text-center">
                  <div className={`w-16 h-16 ${getColorClasses(link.color).split(' ')[0]} rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <link.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {link.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 leading-relaxed">
                    {link.description}
                  </p>
                  <Link
                    to={link.path}
                    className={`inline-flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 ${getColorClasses(link.color).split(' ').slice(2).join(' ')} hover:scale-105`}
                  >
                    Go
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Counters Section */}
      <Counters />

      {/* Testimonials Section */}
      <Testimonials />

      {/* Call to Action Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-aos="fade-up">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Join Our Community?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Discover more about our programs, events, and opportunities for your child's education.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/contact"
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Contact Us
            </Link>
            <Link
              to="/about"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Learn More
            </Link>
            <SocialShare 
              title="Rasoha Academy"
              description="Empowering students with quality education and fostering a culture of excellence"
              url="https://rasohaacademy.com"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 